import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Live L2 order book and trade feed for the Hyperliquid venue.
 *
 * GMX-style pools have no central limit order book, so the book in the design
 * can only be shown for a venue that actually has one. Hyperliquid does, the
 * app already routes orders there (see `lib/api/hyperliquid.ts`), and its
 * `info` endpoint is public and CORS-open — so these are real levels, never
 * synthesised.
 *
 * The endpoint is hit directly rather than through the project's backend proxy
 * because that proxy exposes no book route. To move it behind the proxy later,
 * only `HL_INFO_URL` and the two `post` calls below need to change.
 */
const HL_INFO_URL = "https://api.hyperliquid.xyz/info";

/** Design calls for a 1.4s cadence; the book is cheap but not free. */
const POLL_MS = 2000;
const TRADE_ROWS = 18;

type RawLevel = { px: string; sz: string; n: number };
type RawTrade = { coin: string; side: "A" | "B"; px: string; sz: string; time: number; tid: number };

export type BookRow = {
  price: number;
  size: number;
  /** Cumulative size from the top of the book down to this row. */
  total: number;
  /** Share of the largest row on either side, 0–1 — drives the depth bar. */
  depth: number;
};

export type TradeRow = { price: number; size: number; time: number; isBuy: boolean };

export type HyperliquidBook = {
  asks: BookRow[];
  bids: BookRow[];
  trades: TradeRow[];
  /** Midpoint of best bid and best ask, or undefined before the first fetch. */
  mid: number | undefined;
  isLoading: boolean;
  /** Set when the venue has no book for this coin, or the feed is unreachable. */
  error: string | undefined;
};

const EMPTY: HyperliquidBook = {
  asks: [],
  bids: [],
  trades: [],
  mid: undefined,
  isLoading: false,
  error: undefined,
};

async function post<T>(body: unknown, signal: AbortSignal): Promise<T> {
  const res = await fetch(HL_INFO_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    throw new Error(`Hyperliquid info ${res.status}`);
  }

  return res.json() as Promise<T>;
}

/**
 * Accumulates size down each side and normalises against the deepest row so
 * both sides share one scale, as in the design.
 */
function toRows(levels: RawLevel[] | undefined, rows: number, maxSize: number): BookRow[] {
  if (!levels?.length) {
    return [];
  }

  let cumulative = 0;

  return levels.slice(0, rows).map((level) => {
    const size = Number(level.sz);
    cumulative += size;

    return {
      price: Number(level.px),
      size,
      total: cumulative,
      depth: maxSize > 0 ? Math.min(1, size / maxSize) : 0,
    };
  });
}

export function useHyperliquidBook(coin: string | undefined, rows: number, enabled = true): HyperliquidBook {
  const [state, setState] = useState<HyperliquidBook>(EMPTY);
  /* Keeps the previous book on screen while the next poll is in flight. */
  const hasDataRef = useRef(false);

  useEffect(() => {
    if (!coin || !enabled) {
      hasDataRef.current = false;
      setState(EMPTY);
      return;
    }

    hasDataRef.current = false;
    setState({ ...EMPTY, isLoading: true });

    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      /* Polling a hidden tab burns rate limit for nothing. */
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }

      try {
        const [book, trades] = await Promise.all([
          post<{ levels?: [RawLevel[], RawLevel[]] }>({ type: "l2Book", coin }, controller.signal),
          post<RawTrade[]>({ type: "recentTrades", coin }, controller.signal),
        ]);

        if (cancelled) {
          return;
        }

        const rawBids = book.levels?.[0] ?? [];
        const rawAsks = book.levels?.[1] ?? [];

        if (!rawBids.length && !rawAsks.length) {
          setState({ ...EMPTY, error: `No ${coin} book on Hyperliquid` });
          return;
        }

        const maxSize = Math.max(
          ...rawBids.slice(0, rows).map((l) => Number(l.sz)),
          ...rawAsks.slice(0, rows).map((l) => Number(l.sz))
        );

        const bids = toRows(rawBids, rows, maxSize);
        /* Asks read top-down from the highest price, so the far side comes first. */
        const asks = toRows(rawAsks, rows, maxSize).reverse();

        const bestBid = bids[0]?.price;
        const bestAsk = asks[asks.length - 1]?.price;

        hasDataRef.current = true;
        setState({
          asks,
          bids,
          trades: (Array.isArray(trades) ? trades : []).slice(0, TRADE_ROWS).map((t) => ({
            price: Number(t.px),
            size: Number(t.sz),
            time: t.time,
            /* Hyperliquid tags the aggressor: "B" bought into the ask. */
            isBuy: t.side === "B",
          })),
          mid: bestBid !== undefined && bestAsk !== undefined ? (bestBid + bestAsk) / 2 : bestBid ?? bestAsk,
          isLoading: false,
          error: undefined,
        });
      } catch (e) {
        if (cancelled || controller.signal.aborted) {
          return;
        }

        /* A failed poll must not blank a book that is already on screen. */
        if (!hasDataRef.current) {
          setState({ ...EMPTY, error: e instanceof Error ? e.message : "Book unavailable" });
        }
      }
    };

    load();
    const timer = setInterval(load, POLL_MS);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
    };
  }, [coin, rows, enabled]);

  return useMemo(() => state, [state]);
}
